import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetSetsQuery } from '../../entities/sets/sets.api';
import { selectUser } from '../../store/user/user.selector';

// styles
import {
  SetsPageContainer,
  SetListContainer,
  Header,
  LeftContent,
  CheckboxContainer,
  MultiSelectCheckbox,
  RightContent,
  ButtonContainer,
  SetsList,
  EmptyState
} from './SetsPage.styles.js';

// components
import CreateSetForm from './components/create-set-form/CreateSetForm.js';
import SetCard from './components/set-card/SetCard.js';
import Title from '../../components/widgets/components/title/Title.js';
import PrimaryBtn from '../../components/btns/primary-btn/PrimaryBtn.js';
import InputBar from '../../components/input-bar/InputBar.js';
import CombineModal from './components/combine-modal/CombineModal.js';

export default function SetsPage() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [selectedSets, setSelectedSets] = useState(new Set());
  const [showCombineModal, setShowCombineModal] = useState(false);
  const [combineFormData, setCombineFormData] = useState({
    title: '',
    description: '',
    tags: []
  });
  const user = useSelector(selectUser);
  const { data: sets = [], isLoading, error } = useGetSetsQuery(user?.uid);

  const handleCreateSuccess = () => {
    setShowCreateForm(false);
  };

  const handleMultiSelectToggle = () => {
    setIsMultiSelectMode(!isMultiSelectMode);
    setSelectedSets(new Set()); // Clear selections when toggling mode
  };

  const handleSetSelect = (setId) => {
    const newSelectedSets = new Set(selectedSets);
    if (newSelectedSets.has(setId)) {
      newSelectedSets.delete(setId);
    } else {
      newSelectedSets.add(setId);
    }
    setSelectedSets(newSelectedSets);
  };

  const handleDeleteSelected = async () => {
    if (!user?.uid) {
      console.error('User not authenticated');
      return;
    }

    try {
      const setIds = Array.from(selectedSets);
      if (setIds.length === 0) {
        console.warn('No sets selected for deletion');
        return;
      }

      setSelectedSets(new Set());
      setIsMultiSelectMode(false);
    } catch (error) {
      // Error deleting sets
      // TODO: Add user notification for error
    }
  };

  const handleCombineClick = () => {
    setShowCombineModal(true);
    // Pre-populate with suggested name based on selected sets
    const selectedSetTitles = Array.from(selectedSets).map(setId =>
      sets.find(set => set.id === setId)?.title
    ).filter(Boolean);

    // Combine all tags from selected sets
    const allTags = Array.from(selectedSets).reduce((combinedTags, setId) => {
      const set = sets.find(s => s.id === setId);
      if (set?.tags && Array.isArray(set.tags)) {
        return [...combinedTags, ...set.tags];
      }
      return combinedTags;
    }, []);

    // Remove duplicate tags
    const uniqueTags = [...new Set(allTags)];

    setCombineFormData({
      title: selectedSetTitles.join(' + '),
      description: `Combined set from: ${selectedSetTitles.join(', ')}`,
      tags: uniqueTags
    });
  };

  const handleCombineCancel = () => {
    setShowCombineModal(false);
    setCombineFormData({ title: '', description: '', tags: [] });
  };

  const handleCombineSubmit = async () => {
    if (!user?.uid) {
      console.error('User not authenticated');
      return;
    }

    try {
      const setIds = Array.from(selectedSets);
      if (setIds.length < 2) {
        console.warn('At least 2 sets required for merging');
        return;
      }

      if (!combineFormData.title.trim()) {
        console.warn('Set title is required');
        return;
      }

      setShowCombineModal(false);
      setSelectedSets(new Set());
      setIsMultiSelectMode(false);
      setCombineFormData({ title: '', description: '', tags: [] });
    } catch (error) {
      // Error merging sets
      // TODO: Add user notification for error
    }
  };

  // Filter sets based on search term
  const filteredSets = sets.filter(set =>
    set.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (set.description && set.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (set.tags && set.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  if (showCreateForm) {
    return (
      <SetsPageContainer>
        <CreateSetForm
          onCancel={() => setShowCreateForm(false)}
          onSuccess={handleCreateSuccess}
        />
      </SetsPageContainer>
    );
  }

  return (
    <SetsPageContainer>
      <SetListContainer className='widget-bg'>
        <Header>
          <LeftContent>
            <Title text={`MY SETS (${sets.length})`} />
            <CheckboxContainer>
              <MultiSelectCheckbox
                type="checkbox"
                checked={isMultiSelectMode}
                onChange={handleMultiSelectToggle}
              />
              <h4>Select Multiple Sets</h4>
            </CheckboxContainer>
            <ButtonContainer>
              {selectedSets.size > 0 && (
                <PrimaryBtn
                  title={`DELETE (${selectedSets.size})`}
                  onClick={handleDeleteSelected}
                />
              )}
              {selectedSets.size > 1 && (
                <PrimaryBtn
                  title={`COMBINE (${selectedSets.size})`}
                  onClick={handleCombineClick}
                />
              )}
            </ButtonContainer>
          </LeftContent>
          <RightContent>
            <InputBar
              type="text"
              id="search-sets"
              label="Search sets by name, description, or tags..."
              value={searchTerm}
              updateValue={setSearchTerm}
              expands={false}
            />
            <PrimaryBtn title="+ CREATE SET" onClick={() => setShowCreateForm(true)} />
          </RightContent>
        </Header>

        {isLoading && <h3>Loading sets...</h3>}

        {error && <h3>Error loading sets: {error.message}</h3>}

        {sets.length === 0 && !isLoading ? (
          <EmptyState>
            <h2>No sets created yet</h2>
            <p>Create your first set to start organizing your cards!</p>
            <PrimaryBtn title="Create Your First Set" onClick={() => setShowCreateForm(true)} />
          </EmptyState>
        ) : filteredSets.length === 0 ? (
          <EmptyState>
            <h2>No sets found</h2>
            <p>No sets match your search term "{searchTerm}".</p>
          </EmptyState>
        ) : (
          <SetsList>
            {filteredSets.map((set) => (
              <SetCard
                key={set.id}
                set={set}
                isMultiSelectMode={isMultiSelectMode}
                isSelected={selectedSets.has(set.id)}
                onSelect={() => handleSetSelect(set.id)}
              />
            ))}
          </SetsList>
        )}
      </SetListContainer>

      {/* Combine Modal */}
      {showCombineModal && 
        <CombineModal
          isOpen={showCombineModal}
          onClose={handleCombineCancel}
          onSubmit={handleCombineSubmit}
          formData={combineFormData}
          setFormData={setCombineFormData}
        />
      }
    </SetsPageContainer >
  );
}
